import { useState, useEffect } from "react";

import DOMPurify from "dompurify";

/**
 * Custom hook to use localStorage
 * @param {string} key
 * @param {T} defaultValue
 * @returns {[T, (value: T) => void]}
 */
const useLocalStorage = <T>(key: string, defaultValue: T): [T, (value: T) => void] => {
  const [value, setValue] = useState<T>(() => {
    let currentValue: T;

    try {
      if (typeof defaultValue === "string") {
        currentValue = defaultValue as T;
        return currentValue;
      }

      if (typeof window !== "undefined") {
        const storedValue = localStorage.getItem(key);
        if (storedValue === null || storedValue === "undefined") {
          currentValue = defaultValue;
        } else {
          currentValue = JSON.parse(storedValue);
          if (typeof currentValue === "string" && currentValue.includes("<")) {
            // If it contains HTML-like content, sanitize it
            if (typeof DOMPurify !== "undefined" && DOMPurify.sanitize) {
              currentValue = DOMPurify.sanitize(currentValue, { ALLOWED_TAGS: [] }) as T;
            } else {
              currentValue = currentValue.replace(/<[^>]*>/g, "") as T;
            }
          }
        }
      } else {
        currentValue = defaultValue;
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
      currentValue = defaultValue;
    }

    return currentValue;
  });

  useEffect(() => {
    if (typeof window !== "undefined") {
      localStorage.setItem(key, JSON.stringify(value));
    }
  }, [value, key]);

  return [value, setValue];
};

export { useLocalStorage };
